import { GitBranchAIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Avatar, Badges, Checkbox, ColumnType, Shimmer, Stack, Table, Text } from '@prophecy/ui-v3';
import { TABLE_SELECTION_CELL } from '@prophecy/ui-v3/Table/tokens';
import { formatBytes } from '@prophecy/utils/fmt';
import { useMemo } from 'react';
import { format as timeAgoFormat } from 'timeago.js';

import { BranchStatusBadge } from './BranchStatusBadge';
import { useBranchTab } from './BranchTabContext';
import { getCheckoutDisabledReason, getCheckoutStatuses, isCheckoutSelectable, LocalCheckout } from './types';

const TableText = ({ children }: { children: string | number }) => (
  <Text level='sm13' weight={theme.fontWeight.normal} tone={theme.colors.gray900} data-test-id='table-text'>
    {children}
  </Text>
);

function formatCheckoutSize(bytes: number | null | undefined) {
  if (bytes == null) {
    return '--';
  }
  return formatBytes(bytes);
}

function getColumns({
  selectedIds,
  selectableIds,
  toggleCheckout,
  toggleSelectAll
}: {
  selectedIds: string[];
  selectableIds: string[];
  toggleCheckout: (userRepositoryUid: string) => void;
  toggleSelectAll: () => void;
}): ColumnType<LocalCheckout>[] {
  const selectedSet = new Set(selectedIds);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedSet.has(id));
  const someSelected = selectableIds.some((id) => selectedSet.has(id));

  return [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          disabled={selectableIds.length === 0}
          onChange={toggleSelectAll}
          aria-label='Select all branches'
          data-test-id='branches-select-all'
        />
      ),
      key: 'select',
      width: '50px',
      hideEllipsis: true,
      className: TABLE_SELECTION_CELL,
      render: (_value: unknown, checkout: LocalCheckout) => (
        <Checkbox
          checked={selectedSet.has(checkout.userRepositoryUid)}
          disabled={!isCheckoutSelectable(checkout)}
          tooltip={getCheckoutDisabledReason(checkout)}
          onChange={() => toggleCheckout(checkout.userRepositoryUid)}
          aria-label={`Select ${checkout.branch}`}
          data-test-id={`branches-select-${checkout.userRepositoryUid}`}
        />
      )
    },
    {
      title: 'Branch',
      dataIndex: 'branch',
      key: 'branch',
      render: (_branch: string, checkout: LocalCheckout) => (
        <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
          <GitBranchAIcon type='default' size='xs' />
          <Text level='sm'>{checkout.branch}</Text>
          {checkout.isMainBranch ? (
            <Badges variant='pillLight' size='xs' tone='gray'>
              Default
            </Badges>
          ) : null}
        </Stack>
      )
    },
    {
      title: 'Last commit',
      dataIndex: ['detail', 'lastCommitAuthor'],
      key: 'lastCommitAuthor',
      width: '220px',
      render: (_value: string | null, checkout: LocalCheckout) => {
        const author = checkout.detail?.lastCommitAuthor;
        if (!author) {
          return <TableText>--</TableText>;
        }
        return (
          <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
            <Avatar size='xs' text={author} />
            <TableText>{author}</TableText>
          </Stack>
        );
      }
    },
    {
      title: 'Updated',
      dataIndex: ['detail', 'lastCommittedAt'],
      key: 'lastCommittedAt',
      width: '140px',
      render: (_value: string | null, checkout: LocalCheckout) => {
        const updatedAt = checkout.detail?.lastCommittedAt;
        return <TableText>{updatedAt ? timeAgoFormat(updatedAt) : '--'}</TableText>;
      }
    },
    {
      title: 'Status',
      key: 'status',
      render: (_value: unknown, checkout: LocalCheckout) => (
        <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
          {getCheckoutStatuses(checkout).map((status) => (
            <BranchStatusBadge key={status.type} status={status} />
          ))}
        </Stack>
      )
    },
    {
      title: 'Size',
      dataIndex: ['detail', 'sizeOnDiskBytes'],
      key: 'sizeOnDiskBytes',
      width: '100px',
      render: (_value: number | null, checkout: LocalCheckout) => (
        <TableText>{formatCheckoutSize(checkout.detail?.sizeOnDiskBytes)}</TableText>
      )
    }
  ];
}

const scrollConfig = {
  y: 330
} as const;

export const BranchesTable = () => {
  const checkouts = useBranchTab(({ state }) => state.checkouts);
  const loading = useBranchTab(({ state }) => state.loading);
  const selectedIds = useBranchTab(({ state }) => state.selectedIds);
  const toggleCheckout = useBranchTab(({ actions }) => actions.toggleCheckout);
  const toggleSelectAll = useBranchTab(({ actions }) => actions.toggleSelectAll);

  const selectableIds = useMemo(
    () => checkouts.filter(isCheckoutSelectable).map((checkout) => checkout.userRepositoryUid),
    [checkouts]
  );

  const columns = useMemo(
    () =>
      getColumns({
        selectedIds,
        selectableIds,
        toggleCheckout,
        toggleSelectAll
      }),
    [selectedIds, selectableIds, toggleCheckout, toggleSelectAll]
  );
  return (
    <Shimmer loading={loading} fullWidth data-test-id='branches-table-loading'>
      <Table
        columns={columns}
        dataSource={checkouts}
        rowKey='userRepositoryUid'
        scroll={scrollConfig}
        data-test-id='branches-table'
      />
    </Shimmer>
  );
};
